import {defineStore} from "pinia";
import {ref} from "vue";
import {useUserStore} from "@/app/stores/user-store.ts";
import {ApiException} from "@/api/enterprise-gateway-api.proxy.ts";
import {useRouter} from "vue-router";
import {useToast} from 'primevue/usetoast';

// conform to PrimeVue toast severity types
export enum EAlertType {
    Error = 'error',
    Warning = 'warn',
    Success = 'success',
}

export interface IAlertNotification {
    type: EAlertType;
    id: string;
    title: string;
    message: string;
    pageName?: string;
    icon?: 'cross' | 'warning' | 'info-circle';
    closable?: boolean;
    autoClose?: number;
}

interface ErrorDetails {
    message: string;
    stack?: string;
    timestamp: Date;
    code?: string;
    errors?: string;
}

export const useErrorStore = defineStore('error-store', () => {
    const userStore = useUserStore();
    const router = useRouter();
    const helpdeskMail: string = import.meta.env.VITE_EMAIL_HELPDESK;
    const lastCorrelationId = ref<string>('');
    const lastResponseCode = ref<number>();
    const alerts = ref(new Array<IAlertNotification>());
    const toast = useToast();

    const lastError = ref<ErrorDetails | null>(null);

    const addAlert = (newAlert: IAlertNotification) => {
        if (alerts.value.findIndex((alert) => alert.id === newAlert.id) < 0) {
            alerts.value.push(newAlert);

            // add to PrimeVue toast service
            toast.add({
                severity: newAlert.type,
                summary: newAlert.title,
                detail: newAlert.message,
                life: newAlert.autoClose,
            })
        }
    }

    const removeAlert = (alertId: string) => {
        alerts.value = alerts.value.filter((alert) => alert.id !== alertId);
    }

    const setError = async (error: unknown, redirectToErrorPage: boolean = false) => {
        const createErrorDetails = (message: string, stack?: string, errors?: string): ErrorDetails => ({
            message,
            stack,
            timestamp: new Date(),
            errors
        });

        const handleRedirect = async (errorStatus: string) => {
            if (!redirectToErrorPage) return;
            await router.push({ name: errorStatus });
        };

        const errorHandlers = {
            apiException: (apiException: ApiException) => {
                if(apiException.status === 401) {
                    window.location.assign(`${window.location.origin}/auth/login`);
                }

                lastError.value = createErrorDetails(apiException.message, apiException.stack, apiException.response);
                lastResponseCode.value = apiException.status;
                lastCorrelationId.value = apiException.headers['x-correlation-id'] || '';
                return apiException.status.toString();
            },
            standardError: (err: Error) => {
                lastError.value = createErrorDetails(err.message, err.stack);
                return 'error';
            },
            stringError: (err: string) => {
                lastError.value = createErrorDetails(err);
                return 'error';
            },
            defaultError: () => {
                lastError.value = createErrorDetails('An unknown error occurred');
                return 'error';
            }
        };

        let errorStatus: string;

        if (error instanceof ApiException) {
            errorStatus = errorHandlers.apiException(error);
        } else if (error instanceof Error) {
            errorStatus = errorHandlers.standardError(error);
        } else if (typeof error === 'string') {
            errorStatus = errorHandlers.stringError(error);
        } else {
            errorStatus = errorHandlers.defaultError();
        }

        await handleRedirect(errorStatus);
    };

    const getFormattedError = (): string => {
        if (!lastError.value) return '';

        const { message, timestamp } = lastError.value;
        return `${message} (occurred at ${timestamp.toLocaleString()}) with stack trace: ${lastError.value.stack || 'No stack trace available'}`;
    }

    const mailTo = (page: string, introText: string): string => {
        // Important!! Where it seem there are too many/too little spaces, this is intended. Do not remove these!
        const organisationSubjectPart = `${userStore.user.organisationName ? ` - ${userStore.user.organisationName}` : ''}`
        const userFullName = userStore.fullName;

        const correlationIdPart = `${lastCorrelationId.value ?  `%0D%0A   - CorrelationId: ${lastCorrelationId.value}` : ''}`;
        const errorPart = lastError.value ? `%0D%0A   - Error: ${encodeURIComponent(getFormattedError())}` : '';

        const intro = `${introText} %0D%0ATechnische informatie (verwijder dit deel van de mail niet, het technische team heeft deze nodig):`
        const infoLine = `Gelieve hieronder eventueel een schermafbeelding en een verduidelijking van het probleem toe te voegen (wanneer kwam deze fout voor, welke data werd reeds opgegeven, welke actie(s) werd(en) reeds ondernomen...):`

        return `mailto:${helpdeskMail}?subject=${page}:${userFullName}${organisationSubjectPart}
                &body=${intro} %0D%0A ${correlationIdPart} %0D%0A ${errorPart}%0D%0A
                %0D%0A%0D%0A${infoLine}`;
    }

    return {
        lastCorrelationId,
        lastError,
        alerts,
        setError,
        mailTo,
        addAlert,
        removeAlert
    }
});
